"use client";

import { useEffect, useRef, useState } from "react";
import { useAuth } from "@/contexts/auth-context";
import { redirectToCustomerPortal } from "@/utils/stripe/client";
import { phCapture } from "@/utils/analytics";
import { isPaymentIssue } from "@/lib/billing/status";
import { StatePanel } from "@/components/ui/state-panel";

const BANNER_SHOWN_KEY = "billing_banner_shown_session";

export function BillingIssueBanner() {
  const { subscriptions } = useAuth();
  const hasTrackedRef = useRef(false);
  const [isRedirecting, setIsRedirecting] = useState(false);
  const [portalError, setPortalError] = useState<string | null>(null);
  const [needsSignIn, setNeedsSignIn] = useState(false);

  const hasActiveSub = subscriptions?.some((s) => s.status === "active" || s.status === "trialing");
  const badSub = hasActiveSub ? null : subscriptions?.find((s) => isPaymentIssue(s.status));

  useEffect(() => {
    if (!badSub || hasTrackedRef.current) return;

    try {
      if (typeof window !== "undefined" && window.sessionStorage) {
        const alreadyShown = sessionStorage.getItem(BANNER_SHOWN_KEY);
        if (alreadyShown) return;

        phCapture("billing_banner_shown", {
          subscription_id: badSub.subscription_id,
          status: badSub.status,
        });
        sessionStorage.setItem(BANNER_SHOWN_KEY, "true");
        hasTrackedRef.current = true;
      }
    } catch {
    }
  }, [badSub]);

  if (!badSub) return null;

  const handleFixBilling = async () => {
    if (isRedirecting) return;
    setIsRedirecting(true);
    setPortalError(null);
    setNeedsSignIn(false);
    phCapture("billing_fix_clicked", {
      subscription_id: badSub.subscription_id,
      status: badSub.status,
    });
    try {
      await redirectToCustomerPortal();
    } catch (error) {
      const signInRequired = error instanceof Error && (
        error.message === "No active session found. Please log in again." || error.message === "Unauthorized"
      );
      setNeedsSignIn(signInRequired);
      setPortalError(signInRequired
        ? "Your session has expired. Sign in again to update your payment method."
        : "Unable to open billing. Try again or contact support.");
    } finally {
      setIsRedirecting(false);
    }
  };

  return (
    <StatePanel
      variant={portalError ? "error" : "warning"}
      shape="banner"
      title="Payment issue"
      message={portalError ?? "We couldn't process your last payment. Update your payment method to keep access."}
      retry={{
        label: isRedirecting ? "Opening billing…" : "Fix billing",
        isLoading: isRedirecting,
        onClick: () => {
          void handleFixBilling();
        },
      }}
      secondary={needsSignIn ? { label: "Sign In", href: "/signin?redirect=%2Ffix-billing" } : undefined}
      className="mx-3 mt-3"
    />
  );
}
